/**
 * relativeDates — sliding-window helpers that keep mock demo data fresh.
 *
 * Why this exists
 * ───────────────
 * The mock dataset in `data/mockData.js`, `data/toolkitEntries.js`, and a few
 * other places carries 200+ hard-coded ISO dates (`'2024-08-01'`, `'2025-01-15'`,
 * etc.). At authoring time those felt "current" because the implied "today" was
 * around mid-January 2025. Months later, every dashboard reads "Last contact:
 * Aug 1, 2024" which feels stale during live demos.
 *
 * Rather than rewriting every literal, we shift them on read. We pick a single
 * `MOCK_ANCHOR_DATE` (the implied "today" of the original mock dataset) and
 * compute `SHIFT_DAYS = today - anchor`. Every literal date passed through
 * `shiftMockDate()` is bumped forward by the same delta, preserving the exact
 * relative ordering and gaps between events — only the absolute calendar
 * position slides forward as real time passes.
 *
 * Anchor selection
 * ────────────────
 * Most `lastContact` values in mockData cluster around 2025-01-15. Some grant
 * period fixtures reach back to 2024-08 (≈5 months earlier) and forward to
 * 2025-06 (≈5 months later). Anchoring at 2025-01-15 keeps the historical
 * stretch in the past and the upcoming stretch in the future, no matter when
 * the demo runs.
 *
 * Safety
 * ──────
 * - Pass-through for invalid / non-ISO strings (returns input unchanged).
 * - Pass-through for `null` / `undefined` (returns input unchanged).
 * - Date-only inputs (YYYY-MM-DD) stay date-only; full ISO inputs stay full ISO.
 * - Pure function, no side effects, safe to call inside `useMemo`.
 */

// Implied "today" of the mock dataset when it was authored. Do not change this
// without also reviewing visual ordering across the mock files.
export const MOCK_ANCHOR_DATE = '2025-01-15';

const DAY_MS = 24 * 60 * 60 * 1000;
const _anchorMs = Date.parse(MOCK_ANCHOR_DATE + 'T00:00:00Z');
// SHIFT_DAYS is captured once at module load — every consumer sees the same
// delta within a single page render, avoiding off-by-one race conditions when
// renders straddle midnight.
export const SHIFT_DAYS = Math.floor((Date.now() - _anchorMs) / DAY_MS);

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME  = /^\d{4}-\d{2}-\d{2}T/;

/**
 * Shift an ISO date / datetime string forward by SHIFT_DAYS.
 *
 *   shiftMockDate('2024-08-01')             → '2025-11-15'  (≈5 months ago today)
 *   shiftMockDate('2025-01-15')             → today
 *   shiftMockDate('2025-06-01T14:00:00Z')   → today + ~5 months at 14:00 UTC
 *
 * Returns the original input untouched for nullish / malformed values.
 */
export function shiftMockDate(value) {
  if (value == null) return value;
  if (typeof value !== 'string') return value;
  const isDateOnly = ISO_DATE_ONLY.test(value);
  const isDateTime = ISO_DATETIME.test(value);
  if (!isDateOnly && !isDateTime) return value;
  const ms = Date.parse(isDateOnly ? value + 'T00:00:00Z' : value);
  if (Number.isNaN(ms)) return value;
  const shifted = new Date(ms + SHIFT_DAYS * DAY_MS);
  if (isDateOnly) return shifted.toISOString().slice(0, 10);
  return shifted.toISOString();
}

/** Convenience: today as YYYY-MM-DD (UTC). */
export const today = () => new Date().toISOString().slice(0, 10);

/** Convenience: today minus N days as YYYY-MM-DD. */
export const daysAgo = (n) =>
  new Date(Date.now() - n * DAY_MS).toISOString().slice(0, 10);

/** Convenience: today plus N days as YYYY-MM-DD. */
export const daysAhead = (n) =>
  new Date(Date.now() + n * DAY_MS).toISOString().slice(0, 10);

// ─────────────────────────────────────────────────────────────────────────
//  Canonical label helpers — use these instead of reinventing local versions
//  in every page. All return short, human-readable strings anchored to today.
//  `n` is days (positive integer). `time` is a free-form time-of-day string
//  ("2:30 PM"). All output is localized in en-US to keep labels stable.
// ─────────────────────────────────────────────────────────────────────────

const _shortDate = (iso) =>
  new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "May 12" — N days in the past. Used for "Last contact / Completed on". */
export const pastLabel = (n) => _shortDate(daysAgo(n));

/** "May 28" — N days in the future. Used for "Next review / Target". */
export const futureLabel = (n) => _shortDate(daysAhead(n));

/** "Due May 28" — convenience wrapper around futureLabel. */
export const dueIn = (n) => `Due ${futureLabel(n)}`;

/** "May 12" alias for closed/resolved items. */
export const completedOn = (n) => pastLabel(n);

/**
 * "Today 2:30 PM" / "Yesterday 10:15 AM" / "May 14, 3:45 PM" — for activity
 * feeds where the time-of-day is meaningful but the calendar date should
 * always look recent.
 */
export const atRelative = (n, time) => {
  if (n <= 0) return `Today ${time}`;
  if (n === 1) return `Yesterday ${time}`;
  return `${pastLabel(n)}, ${time}`;
};

/**
 * "in 3 days" / "in 2 weeks" / "in 1 month" — natural-language phrasing for
 * narrative note contents where exact calendar dates feel too formal.
 */
export const inWords = (n) => {
  if (n <= 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n < 14) return `in ${n} days`;
  if (n < 60) return `in ${Math.round(n / 7)} weeks`;
  if (n < 365) return `in ${Math.round(n / 30)} months`;
  return `in ${Math.round(n / 365)} years`;
};

/** Mirror of `inWords` for the past tense. "3 days ago" / "2 weeks ago". */
export const agoWords = (n) => {
  if (n <= 0) return 'just now';
  if (n === 1) return 'yesterday';
  if (n < 14) return `${n} days ago`;
  if (n < 60) return `${Math.round(n / 7)} weeks ago`;
  if (n < 365) return `${Math.round(n / 30)} months ago`;
  return `${Math.round(n / 365)} years ago`;
};

/**
 * Walk an object/array recursively and shift any string value that looks like
 * an ISO date for the given set of keys. This lets us migrate large mock
 * fixtures without rewriting hundreds of literals one-by-one.
 *
 *   shiftMockFields(neighbors, ['lastContact', 'nextAppointment'])
 *
 * Mutates the input in place (cheap on large mock arrays). Returns the input
 * for chaining.
 */
export function shiftMockFields(obj, keys) {
  const keySet = new Set(keys);
  const walk = (node) => {
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    if (node && typeof node === 'object') {
      for (const k of Object.keys(node)) {
        const v = node[k];
        if (keySet.has(k) && typeof v === 'string') {
          node[k] = shiftMockDate(v);
        } else if (v && (typeof v === 'object')) {
          walk(v);
        }
      }
    }
  };
  walk(obj);
  return obj;
}
